import { Component, Input, Output, EventEmitter, TemplateRef, ContentChild, OnInit, OnChanges, SimpleChanges, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface DataTableColumn<T = any> {
  key: string;
  header: string;
  width?: string;
  minWidth?: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  bold?: boolean;
  formatter?: (row: T) => string;
}

export interface PageChangeEvent {
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './data-table.html',
  styleUrl: './data-table.scss',
})
export class DataTable<T = any> implements OnInit, OnChanges {
  @Input() columns: DataTableColumn<T>[] = [];

  @Input() set data(value: T[]) {
    this._data.set(value ?? []);
  }
  get data(): T[] {
    return this._data();
  }
  private _data = signal<T[]>([]);

  @Input() trackBy: (item: T) => any = (item: any) => item;
  @Input() showCheckbox = true;
  @Input() radioMode = false;
  @Input() isSelected: (item: T) => boolean = () => false;
  @Input() emptyMessage = 'No records found.';

  // Filter
  @Input() filterable = true;
  @Input() filterPlaceholder = 'Type to filter...';
  @Input() filterKeys?: (keyof T)[];

  @Input() showPagination = true;
  @Input() pageSizeOptions: number[] = [10, 25, 50];
  @Input() defaultPageSize = 10;

  @Input() set serverSide(value: boolean) {
    this._serverSide.set(!!value);
  }
  get serverSide(): boolean {
    return this._serverSide();
  }
  private _serverSide = signal(false);

  @Input() set totalRecords(value: number) {
    this._totalRecords.set(value ?? 0);
  }
  get totalRecords(): number {
    return this._totalRecords();
  }
  private _totalRecords = signal(0);

  @Input() set page(value: number) {
    if (value != null) {
      this.currentPage.set(value);
    }
  }
  get page(): number {
    return this.currentPage();
  }

  // Scroll
  @Input() maxHeight = '480px';

  @Output() rowClick = new EventEmitter<T>();

  @Output() pageOrSizeChange = new EventEmitter<PageChangeEvent>();

  @ContentChild('rowDetail') rowDetailTemplate?: TemplateRef<any>;

  protected filterText = signal<string>('');
  protected pageSize = signal<number>(10);
  protected currentPage = signal<number>(1);

  protected sortField = signal<string | null>(null);
  protected sortAsc = signal<boolean>(true);

  protected isFiltered = computed(() => this.filterText().trim().length > 0);

  // "out of X total" — uses server total when server-side, raw loaded length otherwise
  protected totalRecordCount = computed(() =>
    this.serverSide ? this._totalRecords() : this._data().length
  );

  ngOnInit(): void {
    this.pageSize.set(this.defaultPageSize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (!this.serverSide) {
        this.currentPage.set(1);
        this.sortField.set(null);
        this.sortAsc.set(true);
      } else if (this._data().length === 0) {
        this.sortField.set(null);
        this.sortAsc.set(true);
        this.filterText.set('');
      }
    }
  }


  private normalize(s: string): string {
  return s
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212]/g, '-') // all dash variants -> '-'
    .replace(/[\/.\s]+/g, '-');             // / . space -> '-'
}

protected filteredData = computed(() => {
  const term = this.normalize(this.filterText().trim());
  const rows = this._data();
  if (!term) return rows;

  const cols = this.filterKeys
    ? this.columns.filter((c) => this.filterKeys!.includes(c.key as keyof T))
    : this.columns;

  return rows.filter((row) =>
    cols.some((col) => {
      const raw = (row as any)[col.key];
      if (raw == null || raw === '') return false;

      const display = col.formatter ? col.formatter(row) : '';

      return (
        this.normalize(display).includes(term) ||
        this.normalize(String(raw)).includes(term)
      );
    })
  );
});

  protected sortedData = computed(() => {
    const field = this.sortField();
    const rows = this.filteredData();
    if (!field) return rows;

    const asc = this.sortAsc();
    return [...rows].sort((a, b) => {
      const valA = ((a as any)[field] ?? '').toString().toLowerCase();
      const valB = ((b as any)[field] ?? '').toString().toLowerCase();
      return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  });

  protected totalPages = computed(() => {
    const total = this.serverSide ? this._totalRecords() : this.sortedData().length;
    return Math.max(1, Math.ceil(total / this.pageSize()));
  });

  protected pagedData = computed(() => {
    if (this.serverSide) {
      return this.sortedData(); 
    }
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.sortedData().slice(start, start + this.pageSize());
  });

  protected rangeStart = computed(() => {
    const total = this.serverSide ? this._totalRecords() : this.sortedData().length;
    return total === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1;
  });

  protected rangeEnd = computed(() => {
    const total = this.serverSide ? this._totalRecords() : this.sortedData().length;
    return Math.min(this.currentPage() * this.pageSize(), total);
  });

  protected filteredRangeStart = computed(() => {
    const count = this.sortedData().length;
    return count === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1;
  });

  protected filteredRangeEnd = computed(() => {
    const count = this.sortedData().length;
    return Math.min(this.currentPage() * this.pageSize(), count);
  });

  onFilterInput(event: Event): void {
    this.filterText.set((event.target as HTMLInputElement).value);
    this.currentPage.set(1);
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    this.pageSize.set(newSize);
    this.currentPage.set(1);
    if (this.serverSide) {
      this.pageOrSizeChange.emit({ page: 1, pageSize: newSize });
    }
  }

  onSortClick(column: DataTableColumn<T>): void {
    if (!column.sortable) return;

    if (this.sortField() === column.key) {
      this.sortAsc.set(!this.sortAsc());
    } else {
      this.sortField.set(column.key);
      this.sortAsc.set(true);
    }
    this.currentPage.set(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
    if (this.serverSide) {
      this.pageOrSizeChange.emit({ page, pageSize: this.pageSize() });
    }
  }

  goToFirstPage(): void { this.goToPage(1); }
  goToLastPage(): void { this.goToPage(this.totalPages()); }
  goToPrevPage(): void { this.goToPage(this.currentPage() - 1); }
  goToNextPage(): void { this.goToPage(this.currentPage() + 1); }

  onRadioClick(event: Event, item: T): void {
  event.stopPropagation();
  this.onRowClick(item);
}

onRowClick(item: T): void {
  this.rowClick.emit(item);
}

  getCellValue(item: T, column: DataTableColumn<T>): string {
    if (column.formatter) {
      return column.formatter(item);
    }
    const value = (item as any)[column.key];
    return value ?? '—';
  }

  totalColspan(): number {
    return this.columns.length + (this.showCheckbox ? 1 : 0);
  }
}